import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ConfirmDeleteDialog } from "@/lib/Components/Dialog/ConfirmDeleteDialog";
import {
  ArchiveBoxIcon,
  DocumentDuplicateIcon,
  FlagIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "@tanstack/react-router";
import { AssignOrganisationDialog } from "@/app/Suppliers/Components/AssignOrganisationDialog";
import { LinkIcon } from "lucide-react";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  archiveSupplierMutation,
  deleteSupplierMutation,
} from "@/app/Suppliers/GraphQL/supplierMutations";
import { SupplierActionFieldsFragment } from "@/gql/graphql";
import { FeatureFlagDialog } from "@/app/Meta/Components/FeatureFlagDialog";
import { DuplicateSupplierDialog } from "@/app/Suppliers/Components/DuplicateSupplierDialog";
import { graphql } from "@/gql";

graphql(`
  fragment SupplierActionFields on Supplier {
    id
    name
    managed_by_id
  }
`);

export function useSupplierActions(): ActionsHook<SupplierActionFieldsFragment> {
  const { open } = useDialog(ConfirmDeleteDialog);
  const { mutateAsync } = useGqlMutation(deleteSupplierMutation);
  const { mutateAsync: archiveSupplier } = useGqlMutation(
    archiveSupplierMutation,
  );
  const navigate = useNavigate();
  const { isAdmin } = useTenant();
  const { open: assignOrg } = useDialog(AssignOrganisationDialog);
  const { open: openFeatureFlagDialog } = useDialog(FeatureFlagDialog);
  const { open: openDuplicateDialog } = useDialog(DuplicateSupplierDialog);

  return (model) => {
    return [
      {
        label: "Actions",
        items: [
          {
            actions: [
              {
                label: "View relocations",
                Icon: ArrowTopRightOnSquareIcon,
                handleAction: () => {
                  window.open(
                    new URL(
                      `/relocations/?supplier_id=${model.id}`,
                      import.meta.env.VITE_APP_URL,
                    ),
                    "_blank",
                  );
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Assign organisation",
                handleAction: () => {
                  assignOrg({
                    supplierId: model.id,
                  });
                },
                Icon: LinkIcon,
                isHiddenTopBar: true,
                isHidden: !isAdmin,
                isAdminAction: true,
              },
              {
                label: "Organisation Feature Flags",
                Icon: FlagIcon,
                isHiddenTopBar: true,
                isHidden: !isAdmin,
                isAdminAction: true,
                handleAction: () => {
                  openFeatureFlagDialog({
                    organisationId: model.managed_by_id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Duplicate Supplier",
                Icon: DocumentDuplicateIcon,
                isHiddenTopBar: true,
                isHidden: !isAdmin,
                isAdminAction: true,
                handleAction: () => {
                  openDuplicateDialog({
                    supplierId: model.id,
                    supplierName: model.name,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Archive",
                Icon: ArchiveBoxIcon,
                color: "purple",
                isHiddenTopBar: true,
                isAdminAction: true,
                handleAction: () => {
                  open({
                    message:
                      "Are you sure you want to archive this supplier? It will be hidden from supplier lists.",
                    buttonText: `Archive ${model.name}`,
                    title: `Archive ${model.name}`,
                    onDelete: async () => {
                      await archiveSupplier({ id: model.id });
                    },
                  });
                },
              } as const,
            ],
          },
          {
            actions: [
              {
                label: "Delete",
                Icon: TrashIcon,
                color: "red",
                isHiddenTopBar: true,
                isAdminAction: true,
                handleAction: () => {
                  open({
                    message: "Are you sure you want to delete this supplier?",
                    buttonText: `Delete ${model.name}`,
                    title: `Delete ${model.name}`,
                    onDelete: async () => {
                      await mutateAsync({ id: model.id });
                      await navigate({
                        from: "/$tenant/$orgId/supplier/{-$supplierId}",
                        to: "/$tenant/$orgId/supplier/{-$supplierId}/suppliers",
                        params: (prev) => prev,
                      });
                    },
                  });
                },
              } as const,
            ],
          },
        ],
      },
    ];
  };
}
